//* 부고 공통 레이아웃 컴포넌트
import { FC, useEffect, useMemo, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  bugoKakaoShareContent,
  bugoLinkUrl,
  bugoMessageContentString,
} from '@service/bugo/utils/bugoMessage.util';
import { BugoData } from '@shared/api/bugo/bugo.interface';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { ShowCashAccountType } from '@shared/api/member/member.interface';
import { Button } from '@shared/components/Button';
import { WebShareModal } from '@shared/containers/WebShareModal/WebShareModal';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { Link } from 'react-router-dom';
import { useAsync } from 'react-use';

import { FlowerPromotionBox } from '../FlowerPromotion/FlowerPromoitonBox';
import { AcqMsgSection } from './AcqMsgSection';
import { MemberMoney } from './MemberMoney';
import { ObituaryFeventGrid } from './ObituaryFeventGrid';
import { ObituaryFuneralHomeInfo } from './ObituaryFuneralHomeInfo';
import { ObituaryHeader } from './ObituaryHeader';
import { ObituaryMemberGrid } from './ObituaryMemberGrid';

export interface ObituaryLayoutProps {
  bugo: BugoData;
  forMember?: boolean;
  logoImage: string;
  footerImage: string;
  footerLink?: string;
  showFlower: boolean;
  emptyAccountMessage?: string;
}

export const ObituaryLayout: FC<ObituaryLayoutProps> = function ObituaryLayout({
  bugo,
  forMember,
  logoImage,
  footerImage,
  footerLink,
  showFlower,
  emptyAccountMessage,
}) {
  const openId = `bugo-${bugo._id}`;
  const setShareModalOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  const bottomButton = useMemo(() => {
    if (!showFlower) {
      return (
        <div className="fixed bottom-0 z-20 grid h-14 w-full max-w-inherit grid-cols-1 bg-zinc-800">
          <Button
            className="button-rectangle h-full text-base text-white theme-bg-13"
            onClick={() => setShareModalOpen(true)}
          >
            <span>공유</span>
          </Button>
        </div>
      );
    }
    return (
      <div className="fixed bottom-0 z-20 grid h-14 w-full max-w-inherit grid-cols-2 bg-zinc-800">
        <div className="theme-bg-main">
          <Link
            className="center-box h-full w-full gap-1 text-base font-bold"
            to={`/flower-store/?bugoId=${bugo._id}`}
          >
            <span className="text-white">근조화환</span>
          </Link>
        </div>
        <Button
          className="button-rectangle h-full text-base text-white theme-bg-13"
          onClick={() => setShareModalOpen(true)}
        >
          <span>공유</span>
        </Button>
      </div>
    );
  }, [bugo._id, setShareModalOpen, showFlower]);

  const showCashAccountType = useMemo(() => {
    return bugo.member.showCashAccountType === ShowCashAccountType.All;
  }, [bugo.member.showCashAccountType]);

  const accountExpose = bugo.accountExpose ?? bugo.fevent.accountExpose ?? false;

  const [additionalCashAccountList, setAdditionalCashAccountList] = useState<
    CashAccount[]
  >([]);

  const subCashAccountList = bugo.member.subCashAccounts;
  useEffect(() => {
    if (subCashAccountList && subCashAccountList?.length > 0) {
      setAdditionalCashAccountList(subCashAccountList);
    }
  }, [subCashAccountList]);

  useAsync(async () => {
    if (showCashAccountType) {
      const { data: cashAccountList } = await api_.get<CashAccount[]>(
        `public/fevent/${bugo.fevent._id}/cash-accounts`,
      );

      if (cashAccountList) {
        const filteredCashAccountList = cashAccountList.filter(
          (account) =>
            !bugo.member.cashAccount || account._id !== bugo.member.cashAccount._id,
        );
        setAdditionalCashAccountList(filteredCashAccountList);
      }
    }
  }, [bugo.member.cashAccount, showCashAccountType]);

  const footerImg = <img src={footerImage} className="w-full" />;

  return (
    <div className="max-w-inherit pb-14">
      <div className="center-box h-[60px]">
        <img src={logoImage} className="h-9" />
      </div>
      <ObituaryHeader bugo={bugo} />
      <div className="space-y-6 pt-6">
        <div className="grid grid-cols-auto-1fr gap-x-3 gap-y-3 whitespace-pre-line break-keep px-6 text-16 leading-6">
          <ObituaryMemberGrid bugo={bugo} />
          <div></div>
          <div></div>
          <ObituaryFeventGrid bugo={bugo} />
        </div>
        <ObituaryFuneralHomeInfo bugo={bugo} />
        <div className="px-4">
          <MemberMoney
            cashAccount={bugo.member.cashAccount}
            additionalCashAccountList={additionalCashAccountList}
            forMember={forMember}
            cashAccountEditOpenId={`memberObituary-cashAccountOpen-${bugo._id}`}
            emptyAccountMessage={emptyAccountMessage}
            accountExpose={accountExpose}
          />
        </div>
        {bugo.fevent.useAcqMsg && (
          <div className="px-4">
            <AcqMsgSection
              feventId={bugo.fevent._id}
              memberId={forMember ? bugo.member._id : undefined}
              bugoBrandName={bugo.bugoBrand?.bugoAgency?.corpName}
            />
          </div>
        )}
        {showFlower && (
          <div className="px-4">
            <FlowerPromotionBox bugo={bugo} />
          </div>
        )}
        <div className="w-full">
          {footerLink ? <a href={footerLink}>{footerImg}</a> : footerImg}
        </div>
      </div>
      <WebShareModal
        openId={openId}
        content={bugoMessageContentString(bugo)}
        title={'- 부고'}
        kakaoContent={bugoKakaoShareContent(bugo)}
        linkUrl={bugoLinkUrl(bugo)}
        feventId={bugo?.fevent?._id}
      />
      {bottomButton}
    </div>
  );
};
